import { defineStore } from "pinia";
import keyBy from "lodash/keyBy";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import useSampleFlaskGenerator from "@/composables/sampleFlasks/useSampleFlaskGenerator";
import { useProjectsStore } from "@/store/modules/projects";

const { innerFetch, innerPartialUpdate, innerCreate, innerBulkCreate } =
  crudGenerator(db.models.SampleFlaskPrep);

const { sampleToFlaskGenerator } = useSampleFlaskGenerator();

export const useSampleFlaskPrepsStore = defineStore("sampleFlaskPreps", {
  state: () => ({
    sampleFlaskPreps: {},
    isLoading: false,
  }),
  getters: {
    getSampleFlaskPrep: (state) => (uuid) =>
      state.sampleFlaskPreps[uuid] ?? null,
  },
  actions: {
    async createFromSample(sample) {
      const phase = sample.phaseObject;
      await phase.setRelationships();
      const prep = sampleToFlaskGenerator(
        sample,
        phase.installationObject,
        phase
      );
      this.isLoading = true;
      try {
        const response = await innerCreate(
          prep,
          useProjectsStore().isOfflineState
        );

        this.sampleFlaskPreps[response.sample] = response;
      } finally {
        this.isLoading = false;
      }
    },

    async bulkCreateFromSample(samples) {
      this.isLoading = true;
      try {
        const phase = samples[0].phaseObject;
        await phase.setRelationships();
        const installation = phase.installationObject;
        const preps = await innerBulkCreate(
          samples.map((sample) =>
            sampleToFlaskGenerator(sample, installation, phase)
          ),
          useProjectsStore().isOfflineState
        );
        this.sampleFlaskPreps = {
          ...this.sampleFlaskPreps,
          ...keyBy(preps, "sample"),
        };
      } finally {
        this.isLoading = false;
      }
    },

    async updateSampleFlaskPrep(form) {
      this.isLoading = true;
      try {
        const prep = await innerPartialUpdate(
          form,
          useProjectsStore().isOfflineState
        );
        this.sampleFlaskPreps[prep.sample] = prep;

        return prep;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSampleFlaskPreps(projectUuid) {
      while (this.isLoading) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      this.isLoading = true;
      try {
        return await innerFetch(
          { project: projectUuid },
          useProjectsStore().isOfflineState
        );
      } finally {
        this.isLoading = false;
      }
    },

    async setInstallationFlaskPreps(installationUuid) {
      while (this.isLoading) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      const sampleFlaskPreps = await db.sampleFlaskPreps
        .where("installation")
        .equals(installationUuid)
        .toArray();
      this.sampleFlaskPreps = keyBy(sampleFlaskPreps, "sample");
      return sampleFlaskPreps;
    },
  },
});
